import React, { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { getPreferredSuppliers, downloadPreferredSuppliers } from "@/lib/api";
import { fmtNum, fmtMoney } from "@/lib/format";
import { Pager } from "@/pages/ProcurementSnapshot";
import { Search, Award, Download } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  ["Item Code", "left"], ["Description", "left"], ["Preferred Supplier", "left"],
  ["Lead Time", "right"], ["Avg Unit Cost", "right"], ["Delivered Orders", "right"], ["Suppliers Compared", "right"],
];

const Row = ({ r }) => (
  <tr className="border-t border-[var(--line)] hover:bg-slate-50/60" data-testid={`pref-row-${r.item_no}`}>
    <td className="px-3 py-2 num font-medium text-slate-800">{r.item_no}</td>
    <td className="px-3 py-2 text-slate-600 max-w-[280px] truncate" title={r.desc}>{r.desc || "–"}</td>
    <td className="px-3 py-2 text-slate-800 font-medium"><span className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-emerald-600 shrink-0" />{r.supplier_name || "–"}</span></td>
    <td className="px-3 py-2 num text-right text-slate-700">{r.lead_time != null ? `${fmtNum(r.lead_time, 1)} d` : "–"}</td>
    <td className="px-3 py-2 num text-right text-slate-700">{r.unit_cost != null ? fmtMoney(r.unit_cost) : "–"}</td>
    <td className="px-3 py-2 num text-right text-slate-500">{fmtNum(r.orders)}</td>
    <td className="px-3 py-2 num text-right text-slate-500">{r.supplier_count}</td>
  </tr>
);

export default function PreferredSupplier() {
  const [box, setBox] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const params = { page, page_size: 50, search: search || undefined };
  const { data, isFetching } = useQuery({ queryKey: ["pref-sup", params], queryFn: () => getPreferredSuppliers(params), placeholderData: keepPreviousData });
  const rows = data?.rows || []; const total = data?.total || 0;

  const doExport = async () => {
    setExporting(true);
    toast.info("Exporting preferred suppliers…");
    try { await downloadPreferredSuppliers({ search: search || undefined }); toast.success("Preferred suppliers exported to Excel"); }
    catch { toast.error("Export failed — ensure procurement data is loaded"); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="preferred-supplier-page">
      <div>
        <div className="label-eyebrow">Procurement</div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Preferred Supplier</h1>
        <p className="text-sm text-slate-500 mt-0.5">Best supplier per item from JDE delivered orders — shortest actual lead time first, lowest unit cost breaks ties.</p>
      </div>
      <div className="kcard p-3 flex flex-wrap items-center gap-2">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(box.trim()); }} className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input value={box} onChange={(e) => setBox(e.target.value)} data-testid="pref-search" placeholder="Item code(s), description or supplier… press Enter"
            className="w-full h-9 rounded-md border border-[var(--line)] pl-8 pr-3 text-[13px] outline-none focus:border-slate-400" />
        </form>
        {isFetching && <span className="text-[12px] text-slate-400">Loading…</span>}
        <button onClick={doExport} disabled={exporting} data-testid="pref-export-btn" className="ml-auto h-9 px-3 rounded-md text-white text-[13px] font-medium flex items-center gap-1.5 disabled:opacity-60" style={{ background: "var(--kcsa-red)" }}>
          <Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Export to Excel"}
        </button>
      </div>
      <div className="kcard overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="pref-table">
          <thead className="bg-[var(--surface)]"><tr>
            {COLS.map(([h, a]) => <th key={h} className={`thead-cell px-3 py-2.5 text-${a}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => <Row key={r.item_no} r={r} />)}
            {rows.length === 0 && <tr><td colSpan={COLS.length} className="px-3 py-10 text-center text-slate-400" data-testid="pref-empty">No procurement history found — upload a JDE report on the Procurement Snapshot page.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pages={Math.ceil(total / 50)} total={total} onPage={setPage} />
    </div>
  );
}
